import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {CreateNewMapDialogComponent} from './create-new-map-dialog.component';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {CreateFilterDialogComponent} from './create-filter-dialog.component';
import {FilterListBottomsheetComponent} from './filter-list.bottomsheet.component';
import {MapService} from '../api/services/map.service';
import {FilterService} from '../api/services/filter.service';
import {of} from 'rxjs';
import {FilterDto, MODULE_NAMES} from '@maporium-workspace/shared';
import {PackageService} from '../api/services/package.service';
import {MapRoleService} from '../acl/map-role.service';
import {TranslateService} from '@ngx-translate/core';

@Injectable({providedIn: 'root'})
export class MapDialogsService {

  constructor(private matDialog: MatDialog,
              private matBottomSheet: MatBottomSheet,
              private mapService: MapService,
              private filterService: FilterService,
              private packageService: PackageService,
              private mapRoleService: MapRoleService,
              private translate: TranslateService) {
  }


  edit(map: GraphMap) {
    const ref = this.matDialog.open(CreateNewMapDialogComponent, {data: {map}, width: '35%', autoFocus: 'input'});
    return ref.afterClosed();
  }

  duplicate(map: GraphMap) {
    if (this.isBlockedForRole(map)) return of(false);
    const ref = this.matDialog.open(CreateNewMapDialogComponent, {data: {map, isCopy: true}, width: '35%', autoFocus: 'input'});
    return ref.afterClosed();
  }

  download(map: GraphMap) {
    if (this.isBlockedForRole(map) || !this.packageService.hasModule(MODULE_NAMES.MAP_EXPORT)) return;
    if (map) {
      this.mapService.exportMapWithRelationsRecursive(map).subscribe();
    }
  }

  /** isReadonly is ownership alone, so a manager on someone else's smap still passes. */
  private isBlockedForRole(map: GraphMap): boolean {
    return map.isReadonly === true && !this.mapRoleService.canManage(map);
  }

  deleteConfirmation(map: GraphMap, isReadonly = false, isStoreMap = false) {
    let title: string;
    let icon: string;
    let text: string;
    let confirmText: string;

    if (isStoreMap) {
      title = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.REMOVE_FROM_COLLECTION');
      icon = 'remove';
      text = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.REMOVE_FROM_COLLECTION_MESSAGE', {name: map?.name});
      confirmText = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.REMOVE_FROM_COLLECTION');
    } else if (isReadonly) {
      title = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.REMOVE_SMAP_TITLE');
      icon = 'person_remove';
      text = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.REMOVE_SMAP_MESSAGE', {name: map?.name});
      confirmText = this.translate.instant('CREATE_NODE.REMOVE');
    } else {
      title = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.DELETE_SMAP_TITLE');
      icon = 'delete';
      text = this.translate.instant('MISC_DIALOGS.MAP_DIALOGS_SVC.DELETE_SMAP_MESSAGE', {name: map?.name});
      confirmText = this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM');
    }

    const ref = this.matDialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title,
        icon,
        message: text,
        confirmText,
      }
    });
    return ref.afterClosed();
  }

  createFilter() {
    const ref = this.matDialog.open(CreateFilterDialogComponent);
    return ref.afterClosed();
  }

  editFilter(filter: FilterDto) {
    const ref = this.matDialog.open(CreateFilterDialogComponent, { data: filter });
    return ref.afterClosed();
  }

  manageFilters() {
    const ref = this.matBottomSheet.open(FilterListBottomsheetComponent);
    return ref.afterDismissed();
  }

  deleteFilterConfirmation(filter: FilterDto) {
    const ref = this.matDialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('FILTER_LIST.DELETE_FILTER_TITLE'),
        icon: 'delete',
        message: this.translate.instant('FILTER_LIST.DELETE_FILTER_MESSAGE', {name: filter?.name}),
        confirmText: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM'),
      }
    });
    return ref.afterClosed();
  }
}
